import {
  DEFAULT_MEMBER_FEATURE_FLAGS,
  mergeMemberFeatureFlags,
  DEFAULT_SST_FEATURE_FLAGS,
  mergeSSTFeatureFlags,
  DEFAULT_AUTH_FEATURE_FLAGS,
  mergeAuthFeatureFlags,
  mergeForumsLimits,
} from '@pya-mms/common';
import axios from 'axios';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../store';
import {
  setCommunityLogo,
  setCommunityLogoPublicId,
  setCommunityName,
  setFacebookLoginEnabled,
  setGithubLoginEnabled,
  setGoogleLoginEnabled,
  setMemberFeatureFlags,
  setSSTFeatureFlags,
  setAuthFeatureFlags,
  setSkipLogin2FAAllowed,
  setForumsAudience,
  setForumsLimits,
  setPrimaryColor,
} from '../store/settings';
import theme, { Theme } from '../theme';
import { accessibleOnWhite } from './accessibleColor';

interface useFetchSettingsPayload {
  isSettingsFetching: boolean;
}

const useFetchSettings = (setTheme: (theme?: Theme) => void): useFetchSettingsPayload => {
  const settings = useSelector((state: RootState) => state.settings);
  const dispatch = useDispatch();
  const [isSettingsFetching, setIsSettingsFetching] = useState(true);
  const [values, setValues] = useState({
    communityLogo: null,
    communityName: null,
    primaryColor: null,
    communityLogoPublicId: null,
  });

  const updateTheme = useCallback(
    (primaryColor: string) => {
      // Keep the community colour, but never below AA contrast. Copy instead of
      // mutating the shared theme object.
      const primary = primaryColor ? accessibleOnWhite(primaryColor) : theme.colors.general.primary;
      const newTheme = {
        ...theme,
        colors: { ...theme.colors, general: { ...theme.colors.general, primary } },
      };
      dispatch(setPrimaryColor(primary));
      setTheme(newTheme);
    },
    [dispatch, setTheme]
  );

  useEffect(() => {
    updateTheme(settings.primaryColor);
  }, [settings.primaryColor, updateTheme]);

  useEffect(() => {
    const fetch = async () => {
      try {
        const { data } = await axios.get('/settings');
        const {
          communityLogo,
          communityName,
          primaryColor,
          communityLogoPublicId,
          facebookLoginEnabled,
          googleLoginEnabled,
          githubLoginEnabled,
          memberFeatureFlags,
          sstFeatureFlags,
          authFeatureFlags,
          skipLogin2FAAllowed,
          forumsAudienceTypeSlugs,
          forumsCommentMaxLength,
          forumsCommentEditWindowHours,
        } = data;
        setValues({
          communityLogo,
          communityName,
          primaryColor,
          communityLogoPublicId,
        });

        dispatch(setMemberFeatureFlags(mergeMemberFeatureFlags(memberFeatureFlags ?? DEFAULT_MEMBER_FEATURE_FLAGS)));
        dispatch(setSSTFeatureFlags(mergeSSTFeatureFlags(sstFeatureFlags ?? DEFAULT_SST_FEATURE_FLAGS)));
        dispatch(setAuthFeatureFlags(mergeAuthFeatureFlags(authFeatureFlags ?? DEFAULT_AUTH_FEATURE_FLAGS)));
        dispatch(setSkipLogin2FAAllowed(Boolean(skipLogin2FAAllowed)));
        dispatch(setForumsAudience(Array.isArray(forumsAudienceTypeSlugs) ? forumsAudienceTypeSlugs : []));
        dispatch(setForumsLimits(mergeForumsLimits({ forumsCommentMaxLength, forumsCommentEditWindowHours })));

        if (!communityLogo && !communityName && !primaryColor) {
          return;
        }

        if (primaryColor) {
          updateTheme(primaryColor);
        }

        if (communityName) {
          dispatch(setCommunityName(communityName));
        }

        if (communityLogo) {
          dispatch(setCommunityLogo(communityLogo));
        }

        if (communityLogoPublicId) {
          dispatch(setCommunityLogoPublicId(communityLogoPublicId));
        }

        if (typeof facebookLoginEnabled === 'boolean') {
          dispatch(setFacebookLoginEnabled(facebookLoginEnabled));
        }

        if (typeof googleLoginEnabled === 'boolean') {
          dispatch(setGoogleLoginEnabled(googleLoginEnabled));
        }

        if (typeof githubLoginEnabled === 'boolean') {
          dispatch(setGithubLoginEnabled(githubLoginEnabled));
        }
      } catch (error) {
        console.error('Fetching settings failed: ', error);
      } finally {
        setIsSettingsFetching(false);
      }
    };

    fetch();
  }, [setTheme, dispatch, updateTheme]);

  return { isSettingsFetching, ...values };
};

export default useFetchSettings;
